// src/components/ImpersonationBanner.js
//
// Sticky banner shown whenever the current Firebase session was created
// via an admin impersonation flow. We detect that by reading the ID
// token's custom claims (`impersonatedBy`, `impersonatedByEmail`) which
// are stamped onto the token by the `impersonateUser` Cloud Function.

import React, { useEffect, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import LogoutIcon from '@mui/icons-material/Logout';
import { auth } from '../firebase';
import { signOut, onAuthStateChanged } from 'firebase/auth';

const ImpersonationBanner = () => {
  const [impersonation, setImpersonation] = useState(null); // { actingAs, adminEmail }
  const [exiting, setExiting] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const refresh = async (user) => {
      if (!user) {
        if (!cancelled) setImpersonation(null);
        return;
      }
      try {
        // `false` = use cached token if available; claims don't change mid-session.
        const tokenResult = await user.getIdTokenResult(false);
        const claims = tokenResult.claims || {};
        if (claims.impersonatedBy) {
          if (!cancelled) {
            setImpersonation({
              actingAs: user.email || user.uid,
              adminEmail: claims.impersonatedByEmail || 'admin',
            });
          }
        } else if (!cancelled) {
          setImpersonation(null);
        }
      } catch (e) {
        // If we can't read the token, default to not showing the banner —
        // a missing banner is safer than a stuck banner.
        if (!cancelled) setImpersonation(null);
      }
    };

    const unsub = onAuthStateChanged(auth, refresh);
    return () => {
      cancelled = true;
      unsub();
    };
  }, []);

  const handleExit = async () => {
    if (exiting) return;
    setExiting(true);
    try {
      await signOut(auth);
    } catch (e) {
      console.error('Sign out failed during exit-impersonation:', e);
    }
    window.location.assign('/login?returnTo=/admin');
  };

  if (!impersonation) return null;

  return (
    <Box
      role="alert"
      sx={{
        position: 'sticky',
        top: 0,
        zIndex: 2000,
        width: '100%',
        backgroundColor: '#b71c1c',
        color: '#fff',
        px: 2,
        py: 1,
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        flexWrap: 'wrap',
        boxShadow: 2,
      }}
    >
      <VisibilityIcon fontSize="small" />
      <Typography variant="body2" sx={{ fontWeight: 600, flexGrow: 1 }}>
        Impersonating <strong>{impersonation.actingAs}</strong> as admin{' '}
        <strong>{impersonation.adminEmail}</strong>. Actions you take here
        affect the real user account.
      </Typography>
      <Button
        size="small"
        variant="contained"
        color="inherit"
        startIcon={<LogoutIcon />}
        onClick={handleExit}
        disabled={exiting}
        sx={{
          color: '#b71c1c',
          backgroundColor: '#fff',
          fontWeight: 700,
          '&:hover': { backgroundColor: '#f5f5f5' },
        }}
      >
        {exiting ? 'Exiting…' : 'Exit impersonation'}
      </Button>
    </Box>
  );
};

export default ImpersonationBanner;
